import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Checkbox, Col, Input, InputNumber, List, Modal, Radio, Row, Select, Space, Spin,
  Tooltip, Typography, message,
} from 'antd';
import { PauseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import GcPagina from '../components/Layout/GcPagina';
import { FctAviso, FctDato, FctTarjeta, fmtFecha, fmtFechaHora } from '../fct/react';
import { fct } from '../fct/tokens/tokens';
import {
  CasoAltaDetalle, CatalogosAltas, ClienteCoincidente, ClienteExistenteFicha, EstadoCaso,
  GuardarCasoRequest, OrigenCaso, actualizarExistente, anadirNota, cambiarEspera, descartarCaso,
  guardarCaso, interpretarError, leerCaso, leerCatalogos, leerFichaExistente, validarCaso,
} from '../api/altasCasosApi';
import { EstadoCasoTag, OrigenTag, EmailVerificadoBadge } from '../components/Altas/casos/tags';
import { FilaComparada } from '../components/Altas/casos/FilaComparada';
import { BusquedaCoincidentesModal } from '../components/Altas/casos/BusquedaCoincidentesModal';
import { formatearRef } from '../components/Altas/casos/estado';

const { Text } = Typography;

/**
 * Solo lectura como dato (R-12, regla 8 del manual): con el caso cerrado (`lectura`), el campo del
 * provisional se lee como dato (`FctDato`) en vez de como control gris con candado; en edición, el
 * control de siempre. La etiqueta de la fila ya la pone `FilaComparada` (columna izquierda), por eso
 * el dato va sin etiqueta propia. `mono` para códigos, teléfonos y cifras. Vive fuera del componente
 * de página para que su identidad no cambie en cada render (si no, los controles se remontarían).
 */
const CampoOLectura: React.FC<{ lectura: boolean; valor?: React.ReactNode; mono?: boolean; children: React.ReactNode }> =
  ({ lectura, valor, mono, children }) => (lectura ? <FctDato etiqueta="" valor={valor || undefined} mono={mono} /> : <>{children}</>);

/** Aviso de requisitos incumplidos en lista (ajuste 12: Modal.warning con detalle, nunca botones grises). */
const avisarFaltas = (titulo: string, faltas: string[]) =>
  Modal.warning({
    title: titulo,
    content: (
      <ul style={{ paddingLeft: 20 }}>
        {faltas.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>
    ),
    width: 520,
  });

/**
 * Pantalla 2 — Tratamiento del caso (F1.4, spec §6; GESCL-T01 tramo 5).
 * Layout a 2 columnas del mockup validado: rejilla de comparación dato a dato a la izquierda
 * (FilaComparada, copiada con revisión), acciones + estado/notas en la columna derecha.
 * Copia SOLO campo a campo izq→dcha (ajuste 8), cabecera de columnas con banda destacada
 * (ajuste 4), secciones en FctTarjeta, salidas SIEMPRE habilitadas con requisitos
 * comprobados al ejecutar (ajuste 12).
 * En T01 solo la salida "Validar" ejecuta de verdad; "Actualizar existente" y "Descartar"
 * explican su bloqueo externo al pulsarse (GESCL-T02, Q-T05-1/3) — nunca botones grises mudos.
 */
const AltasCasoPage: React.FC = () => {
  const { idCaso: idCasoParam } = useParams();
  const idCaso = Number(idCasoParam);
  const navigate = useNavigate();

  const [caso, setCaso] = useState<CasoAltaDetalle | null>(null);
  const [catalogos, setCatalogos] = useState<CatalogosAltas | null>(null);
  const [datos, setDatos] = useState<GuardarCasoRequest | null>(null);
  const [existente, setExistente] = useState<ClienteExistenteFicha | null>(null);
  /** Valores copiados campo a campo sobre el existente (solo visual en T01; persisten con la salida 2 en GESCL-T02). */
  const [copiados, setCopiados] = useState<Record<string, string>>({});
  /** Tratamiento del existente: selector propio (la ficha de GC_pr_CLIENTE_READ no trae tratamiento). */
  const [tratamientoExistente, setTratamientoExistente] = useState<number | undefined>(undefined);
  const [busquedaAbierta, setBusquedaAbierta] = useState(false);
  const [notaNueva, setNotaNueva] = useState('');
  /** Clave de la acción en curso (validar/actualizar/descartar/nota/espera) → spinner solo en su botón. */
  const [accionEnCurso, setAccionEnCurso] = useState<string | null>(null);
  const ocupado = accionEnCurso !== null;
  const [cargando, setCargando] = useState(true);

  const soloLectura = caso?.esFinal ?? false;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [c, cat] = await Promise.all([leerCaso(idCaso), leerCatalogos()]);
      setCaso(c);
      setCatalogos(cat);
      setDatos({
        idTratamiento: c.idTratamiento,
        nombre: c.nombre,
        apellido1: c.apellido1,
        apellido2: c.apellido2,
        anioNacimiento: c.anioNacimiento,
        email: c.email,
        prefijoMovil: c.prefijoMovil,
        movil: c.movil,
        idProvincia: c.idProvincia,
        codigoPostal: c.codigoPostal,
        idPais: c.idPais,
        inversor: c.inversor,
        idPlazoCompra: c.idPlazoCompra,
        boletin: c.boletin,
        notasProcedencia: c.notasProcedencia,
        tramosFacturacion: c.tramosFacturacion,
        provinciasInteres: c.provinciasInteres,
      });
    } catch (error) {
      message.error(interpretarError(error).mensaje);
    } finally {
      setCargando(false);
    }
  }, [idCaso]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /**
   * Refresco dinámico del caso (notas, estado, verificación) SIN spinner de página completa ni
   * recarga de catálogos, y sin pisar el formulario en edición (`datos`). Lo usan las acciones de
   * la tarjeta "Estado y notas" para que se sientan dinámicas (como el mockup) en vez de recargar
   * toda la pantalla.
   */
  const refrescarCaso = useCallback(async () => {
    setCaso(await leerCaso(idCaso));
  }, [idCaso]);

  const setCampo = <K extends keyof GuardarCasoRequest>(campo: K, valor: GuardarCasoRequest[K]) =>
    setDatos((d) => (d ? { ...d, [campo]: valor } : d));

  const copiar = (campo: string, valor: string | null | undefined) =>
    setCopiados((c) => ({ ...c, [campo]: valor ?? '' }));

  /** Valor mostrado en la columna del existente: lo copiado pisa (visualmente) lo cargado. */
  const vistaExistente = (campo: string, deFicha: string | null | undefined) => {
    const copiado = copiados[campo];
    if (copiado !== undefined)
      return <Text mark>{copiado || '—'}</Text>; // marcado = pendiente de consolidar (salida 2, GESCL-T02)
    return deFicha ? <Text>{deFicha}</Text> : <Text type="secondary">—</Text>;
  };

  const fechaLimite = useMemo(() => {
    // Plazo → fecha límite calculada, no editable (spec §6.2, regla GDeco §3.2).
    switch (datos?.idPlazoCompra) {
      case 1: return dayjs().add(1, 'year');
      case 2: return dayjs().add(2, 'year');
      case 3: return dayjs().add(3, 'year');
      default: return null;
    }
  }, [datos?.idPlazoCompra]);

  const seleccionarCandidato = async (candidato: ClienteCoincidente) => {
    setBusquedaAbierta(false);
    if (!candidato.idCodigoGf) {
      message.warning('El cliente seleccionado no tiene código GF; no se puede cargar su ficha.');
      return;
    }
    try {
      setExistente(await leerFichaExistente(candidato.idCodigoGf));
      setCopiados({});
      setTratamientoExistente(undefined);
    } catch (error) {
      message.error(interpretarError(error).mensaje);
    }
  };

  const ejecutar = async (clave: string, accion: () => Promise<void>, exito: string) => {
    setAccionEnCurso(clave);
    try {
      await accion();
      message.success(exito);
    } catch (error) {
      const { mensaje, faltas } = interpretarError(error);
      if (faltas?.length) avisarFaltas('No se puede completar la acción', faltas);
      else message.error(mensaje);
    } finally {
      setAccionEnCurso(null);
    }
  };

  const onEspera = (enEspera: boolean) =>
    ejecutar('espera', async () => {
      // Guarda los datos y cambia el estado; NO añade nota (las notas se añaden solo con "Añadir").
      await guardarCaso(idCaso, datos!);
      await cambiarEspera(idCaso, enEspera);
      // "Guardar en espera" devuelve al listado; "Reabrir" se queda en el caso para seguir tratándolo.
      if (enEspera) navigate('/altas');
      else await refrescarCaso();
    }, enEspera ? 'Caso guardado en espera.' : 'Caso reabierto (Pendiente).');

  const onAnadirNota = () =>
    ejecutar('nota', async () => {
      await anadirNota(idCaso, notaNueva.trim());
      setNotaNueva('');
      await refrescarCaso();
    }, 'Nota añadida.');

  /** Salida 1 — Validar: confirmación (+ check consciente si inversor sin email, ajuste 11). */
  const onValidar = () => {
    if (!datos) return;
    const sinEmail = !datos.email?.trim();
    const requiereExcepcion = datos.inversor && sinEmail;
    let excepcionMarcada = false;

    const lanzar = (excepcionSinEmail: boolean) =>
      ejecutar('validar', async () => {
        const resultado = await validarCaso(idCaso, {
          datos: datos!,
          nota: notaNueva.trim() || undefined,
          excepcionSinEmail,
        });
        setNotaNueva('');
        Modal.success({
          title: 'Registro validado',
          content: (
            <span>
              Cliente <b>{resultado.codigoCl}</b> confirmado
              {resultado.codigoEscritoEnWeb ? ' y comunicado a la web.' : '.'}
              {resultado.idOportunidad && (
                <>
                  {' '}Se ha generado la oportunidad de compra (pendiente de asignar responsable).
                </>
              )}
            </span>
          ),
          onOk: () => navigate('/altas'),
        });
      }, 'Validación completada.');

    Modal.confirm({
      title: 'Validar cliente provisional',
      content: requiereExcepcion ? (
        <Space direction="vertical">
          <span>Se creará el cliente definitivo con los datos de la solicitud.</span>
          <Checkbox onChange={(e) => { excepcionMarcada = e.target.checked; }}>
            Cliente sin email. Validarlo de todas formas
          </Checkbox>
        </Space>
      ) : (
        'Se creará el cliente definitivo con los datos de la solicitud.'
      ),
      okText: 'Validar',
      cancelText: 'Cancelar',
      onOk: () => {
        if (requiereExcepcion && !excepcionMarcada) {
          // El check consciente es la vía: sin él, la propia salida lo detalla en la lista (422).
          avisarFaltas('No se puede validar todavía', [
            'Cliente inversor sin email: marque "Cliente sin email. Validarlo de todas formas" para continuar.',
          ]);
          return;
        }
        lanzar(requiereExcepcion && excepcionMarcada);
      },
    });
  };

  /**
   * Datos que la salida 2 CONSOLIDA = la columna DERECHA (cliente existente), no la izquierda
   * (que se descarta con el provisional). Para cada campo se usa el valor copiado por el gestor
   * (estructurado, tomado de `datos` cuando la clave está en `copiados`) o, si no se copió, el de
   * la ficha del existente. El tratamiento sale del selector propio de la derecha
   * (`tratamientoExistente`). La validación (tratamiento, ficha si inversor, gate email) y el
   * guardado se hacen sobre ESTE objeto.
   */
  const construirDatosExistente = (
    d: GuardarCasoRequest, e: ClienteExistenteFicha,
  ): GuardarCasoRequest => ({
    idTratamiento: tratamientoExistente ?? undefined,
    nombre: 'nombre' in copiados ? d.nombre : e.nombre,
    apellido1: 'apellido1' in copiados ? d.apellido1 : e.apellido1,
    apellido2: 'apellido2' in copiados ? d.apellido2 : e.apellido2,
    anioNacimiento: 'anio' in copiados ? d.anioNacimiento : undefined, // la ficha no expone el año
    email: 'email' in copiados ? d.email : e.email,
    prefijoMovil: 'movil' in copiados ? d.prefijoMovil : undefined,
    movil: 'movil' in copiados ? d.movil : e.telefonoMovil,
    idProvincia: 'provincia' in copiados ? d.idProvincia : (e.idProvincia ?? undefined),
    codigoPostal: 'cp' in copiados ? d.codigoPostal : e.codigoPostal,
    idPais: d.idPais,
    inversor: 'inversor' in copiados ? d.inversor : e.tieneFichaCompra,
    idPlazoCompra: 'plazo' in copiados ? d.idPlazoCompra : (e.idPlazoCompra ?? undefined),
    boletin: 'boletin' in copiados ? d.boletin : false,
    notasProcedencia: undefined,
    tramosFacturacion: 'tramos' in copiados ? d.tramosFacturacion : e.tramosFacturacion.map((t) => t.id),
    provinciasInteres:
      'provinciasInteres' in copiados ? d.provinciasInteres : e.provinciasInteres.map((p) => p.id),
  });

  /** Salida 2 — Actualizar existente: valida y consolida la COLUMNA DERECHA (el existente). */
  const onActualizar = () => {
    if (!datos) return;
    if (!existente) {
      avisarFaltas('No se puede actualizar todavía', [
        'Seleccione primero el cliente existente en el que consolidar (botón "Buscar").',
      ]);
      return;
    }
    const datosDcha = construirDatosExistente(datos, existente);
    const sinEmail = !datosDcha.email?.trim();
    const requiereExcepcion = datosDcha.inversor && sinEmail;
    let excepcionMarcada = false;

    const lanzar = (excepcionSinEmail: boolean) =>
      ejecutar('actualizar', async () => {
        const resultado = await actualizarExistente(idCaso, {
          datos: datosDcha,
          idClienteExistente: existente.idCliente,
          nota: notaNueva.trim() || undefined,
          excepcionSinEmail,
        });
        setNotaNueva('');
        Modal.success({
          title: 'Cliente existente actualizado',
          content: (
            <span>
              Datos consolidados en <b>{resultado.codigoExistente}</b> y provisional eliminado.
              {resultado.idOportunidad && (
                <> Se ha generado la oportunidad de compra (pendiente de asignar responsable).</>
              )}
            </span>
          ),
          onOk: () => navigate('/altas'),
        });
      }, 'Actualización completada.');

    Modal.confirm({
      title: 'Actualizar existente y eliminar provisional',
      content: requiereExcepcion ? (
        <Space direction="vertical">
          <span>
            Se consolidarán los datos en <b>{existente.idCodigoGf}</b>, se reasignarán las
            imputaciones del provisional y éste se eliminará. Esta acción no se puede deshacer.
          </span>
          <Checkbox onChange={(e) => { excepcionMarcada = e.target.checked; }}>
            Cliente sin email. Actualizar de todas formas
          </Checkbox>
        </Space>
      ) : (
        <span>
          Se consolidarán los datos en <b>{existente.idCodigoGf}</b>, se reasignarán las imputaciones
          del provisional y éste se eliminará. Esta acción no se puede deshacer.
        </span>
      ),
      okText: 'Actualizar',
      okType: 'default', // destructiva: peligro sin primario (R-09)
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => {
        if (requiereExcepcion && !excepcionMarcada) {
          avisarFaltas('No se puede actualizar todavía', [
            'Cliente inversor sin email: marque "Cliente sin email. Actualizar de todas formas" para continuar.',
          ]);
          return;
        }
        lanzar(requiereExcepcion && excepcionMarcada);
      },
    });
  };

  /** Salida 3 — Descartar: borra el provisional (GC + GFW) y cierra el caso como "Descartada". */
  const onDescartar = () =>
    Modal.confirm({
      title: 'Descartar alta',
      content:
        'Se eliminará el cliente provisional (en Gescom y en la web) y el caso quedará como ' +
        'Descartada. El caso se conserva como rastro; esta acción no se puede deshacer.',
      okText: 'Descartar',
      okType: 'default', // destructiva: peligro sin primario (R-09)
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () =>
        ejecutar('descartar', async () => {
          await descartarCaso(idCaso, { nota: notaNueva.trim() || undefined });
          setNotaNueva('');
          Modal.success({
            title: 'Alta descartada',
            content: 'El provisional se ha eliminado y el caso queda como Descartada.',
            onOk: () => navigate('/altas'),
          });
        }, 'Alta descartada.'),
    });

  /** Cabecera de contexto (R-06): misma en carga y con el caso; la salida al listado va en `volver`. */
  const cabecera = {
    seccion: 'Clientes',
    titulo: 'Tratamiento del alta',
    volver: { to: '/altas', etiqueta: 'Volver al listado' },
  };

  if (cargando || !caso || !datos || !catalogos) {
    return (
      <GcPagina {...cabecera}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      </GcPagina>
    );
  }

  const opciones = {
    tratamientos: catalogos.tratamientos.map((t) => ({ value: t.id, label: t.descripcion })),
    plazos: catalogos.plazosCompra.map((p) => ({ value: p.id, label: p.descripcion })),
    tramos: catalogos.tramosFacturacion.map((t) => ({
      value: t.id,
      label: t.codigo ? `${t.codigo} — ${t.descripcion}` : t.descripcion,
    })),
    provincias: catalogos.provincias.map((p) => ({ value: p.id, label: p.descripcion })),
  };

  const etiqueta = (lista: { value: number; label: string }[], id: number | null | undefined) =>
    lista.find((o) => o.value === id)?.label;

  const tramosExistente = existente?.tramosFacturacion.map((t) => t.codigo ?? t.descripcion).join(', ');
  const provinciasExistente = existente?.provinciasInteres.map((p) => p.descripcion).join(', ');
  const tramosProvisional = datos.tramosFacturacion.map((id) => etiqueta(opciones.tramos, id) ?? id).join(', ');
  const provinciasProvisionalInteres = datos.provinciasInteres
    .map((id) => etiqueta(opciones.provincias, id) ?? id)
    .join(', ');

  /* Gate de verificación (D22): en origen Web con email presente y NO verificado (verificado != true,
     incluye "sin comprobar") solo se permite Descartar → Validar y Actualizar quedan DESHABILITADOS
     (no en silencio: el banner de arriba lo explica). En no-web no aplica el bloqueo.
     Desde GESCL-T03 el valor llega en vivo de la web al abrir el caso; `null` significa que la web
     no ha respondido (o no existe el registro web) y se distingue en el texto de "no verificado". */
  const esWeb = caso.origen === OrigenCaso.Web;
  const emailPresente = Boolean(datos.email?.trim());
  const emailVerificadoOk = caso.emailVerificado === true;
  const verificacionDesconocida = caso.emailVerificado === null || caso.emailVerificado === undefined;
  const gateWebBloqueado = esWeb && emailPresente && !emailVerificadoOk;

  /** La flecha de copia se pinta siempre (salvo solo lectura) y se habilita al cargar un existente. */
  const copiaDeshabilitada = !existente;

  const siNo = (v: boolean | null | undefined) => (v == null ? undefined : v ? 'Sí' : 'No');

  /** Botón de acción: igual ancho en la fila y texto que salta de línea si no cabe. */
  const estiloBotonAccion: React.CSSProperties = {
    flex: 1,
    height: 'auto',
    minHeight: 48,
    whiteSpace: 'normal',
    paddingTop: 8,
    paddingBottom: 8,
  };

  return (
    // La ref (y el código GF) pasan al contexto de la cabecera; «Volver al listado» es la salida
    // explícita de la cabecera (`volver`), ya no un botón de la tarjeta.
    <GcPagina
      {...cabecera}
      contexto={`${formatearRef(caso.id)}${caso.idCodigoGf ? ` · ${caso.idCodigoGf}` : ''}`}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Cabecera compacta en una línea, sin etiquetas (ajuste 6) */}
        <FctTarjeta>
          <Space size={12} wrap align="center">
            <Text strong className="fct-mono">{fmtFechaHora(caso.fechaAlta)}</Text>
            <OrigenTag origen={caso.origen} />
            <EstadoCasoTag estado={caso.estado} />
            {esWeb && caso.email && (
              <Space size={4}>
                {/* Estado de verificación que mantiene la web, leído en vivo al abrir el caso /
                    recargar el listado (sin botón de reconsulta manual). */}
                <EmailVerificadoBadge verificado={caso.emailVerificado} aplica />
                <Text>
                  {emailVerificadoOk
                    ? 'Email verificado'
                    : verificacionDesconocida
                      ? 'Verificación del email no disponible'
                      : 'Email no verificado'}
                </Text>
              </Space>
            )}
          </Space>
        </FctTarjeta>

        {soloLectura && (
          <FctAviso tipo="info" titulo="Caso finalizado — vista de solo lectura">
            Los estados finales no se reabren (§2 de la especificación). Si hace falta corregir algo, se crea un caso nuevo.
          </FctAviso>
        )}

        {gateWebBloqueado && !soloLectura && (
          <FctAviso tipo="aviso" titulo={verificacionDesconocida ? 'Verificación del email no disponible' : 'Email sin verificar'}>
            {verificacionDesconocida
              ? 'No se ha podido consultar en la web si el email está verificado (la web no responde o el registro web no existe). Mientras tanto solo se permite Descartar (además de notas y En espera); recargue el caso en unos minutos.'
              : 'Mientras el email no esté verificado (D22), solo se permite Descartar (además de notas y En espera). Validar y Actualizar están deshabilitados.'}
          </FctAviso>
        )}

        <Row gutter={16}>
          <Col xs={24} lg={16}>
            {/* Rejilla de comparación dato a dato (spec §6.2) */}
            <FctTarjeta>
              {/* Banda de cabecera de columnas con más peso visual (ajuste 4) */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '130px 1fr 28px 1fr',
                  columnGap: 12,
                  marginBottom: 12,
                  background: fct.superficie.hundida,
                  border: `1px solid ${fct.linea.fila}`,
                  borderRadius: 4,
                  padding: '10px 8px',
                }}
              >
                <div />
                <Text strong style={{ fontSize: 15 }}>Registro provisional</Text>
                <div />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <Text strong style={{ fontSize: 15 }}>Cliente existente</Text>
                  <Space size={8}>
                    {existente && <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{existente.idCodigoGf}</Text>}
                    {!soloLectura && (
                      <Tooltip title="Buscar cliente coincidente (email, teléfono, móvil, nombre)">
                        <Button size="small" icon={<SearchOutlined />} onClick={() => setBusquedaAbierta(true)}>
                          Buscar
                        </Button>
                      </Tooltip>
                    )}
                  </Space>
                </div>
              </div>

              {/* Secciones de la rejilla: tarjetas sin sombra dentro de la tarjeta (manual). */}
              <div style={{ display: 'grid', gap: 16 }}>
                <FctTarjeta titulo="Identificadores">
                  <FilaComparada label="ID" provisional={<Text className="fct-mono">{caso.idCliente ?? '—'}</Text>}
                    existente={vistaExistente('idCliente', existente ? String(existente.idCliente) : null)} />
                  <FilaComparada label="Código" provisional={<Text className="fct-mono">{caso.idCodigoGf ?? '—'}</Text>}
                    existente={vistaExistente('codigo', existente?.idCodigoGf)} />
                  <FilaComparada label="ID Web" provisional={<Text className="fct-mono">{caso.idClienteWeb ?? '—'}</Text>}
                    existente={vistaExistente('idWeb', existente?.idClienteWeb != null ? String(existente.idClienteWeb) : null)} />
                </FctTarjeta>

                <FctTarjeta titulo="Identidad">
                  <FilaComparada label="Tratamiento" obligatorio
                    provisional={
                      <CampoOLectura lectura={soloLectura} valor={etiqueta(opciones.tratamientos, datos.idTratamiento)}>
                        <Select style={{ width: '100%' }} allowClear disabled={soloLectura}
                          placeholder="Sr. / Sra." options={opciones.tratamientos}
                          value={datos.idTratamiento ?? undefined}
                          onChange={(v) => setCampo('idTratamiento', v ?? null)} />
                      </CampoOLectura>
                    }
                    existente={
                      <Select style={{ width: '100%' }} allowClear disabled={soloLectura || !existente}
                        placeholder={existente ? 'Sr. / Sra.' : '—'} options={opciones.tratamientos}
                        value={tratamientoExistente}
                        onChange={(v) => setTratamientoExistente(v ?? undefined)} />
                    }
                    onCopiar={soloLectura ? undefined : () => setTratamientoExistente(datos.idTratamiento ?? undefined)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Nombre" obligatorio
                    provisional={<CampoOLectura lectura={soloLectura} valor={datos.nombre}><Input disabled={soloLectura} value={datos.nombre ?? ''} onChange={(e) => setCampo('nombre', e.target.value)} /></CampoOLectura>}
                    existente={vistaExistente('nombre', existente?.nombre)}
                    onCopiar={soloLectura ? undefined : () => copiar('nombre', datos.nombre)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Primer apellido"
                    provisional={<CampoOLectura lectura={soloLectura} valor={datos.apellido1}><Input disabled={soloLectura} value={datos.apellido1 ?? ''} onChange={(e) => setCampo('apellido1', e.target.value)} /></CampoOLectura>}
                    existente={vistaExistente('apellido1', existente?.apellido1)}
                    onCopiar={soloLectura ? undefined : () => copiar('apellido1', datos.apellido1)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Segundo apellido"
                    provisional={<CampoOLectura lectura={soloLectura} valor={datos.apellido2}><Input disabled={soloLectura} value={datos.apellido2 ?? ''} onChange={(e) => setCampo('apellido2', e.target.value)} /></CampoOLectura>}
                    existente={vistaExistente('apellido2', existente?.apellido2)}
                    onCopiar={soloLectura ? undefined : () => copiar('apellido2', datos.apellido2)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Año de nacimiento"
                    provisional={<CampoOLectura lectura={soloLectura}mono valor={datos.anioNacimiento ?? undefined}><InputNumber disabled={soloLectura} style={{ width: '100%' }} min={1900} max={2100}
                      value={datos.anioNacimiento ?? undefined}
                      onChange={(v) => setCampo('anioNacimiento', v ?? null)} /></CampoOLectura>}
                    existente={vistaExistente('anio', null)}
                    onCopiar={soloLectura ? undefined : () => copiar('anio', datos.anioNacimiento != null ? String(datos.anioNacimiento) : '')}
                    copiarDeshabilitado={copiaDeshabilitada} />
                </FctTarjeta>

                <FctTarjeta titulo="Contacto">
                  <FilaComparada label="Email"
                    provisional={<CampoOLectura lectura={soloLectura} valor={datos.email}><Input disabled={soloLectura} value={datos.email ?? ''} onChange={(e) => setCampo('email', e.target.value)} /></CampoOLectura>}
                    existente={vistaExistente('email', existente?.email)}
                    onCopiar={soloLectura ? undefined : () => copiar('email', datos.email)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Móvil"
                    provisional={
                      <CampoOLectura lectura={soloLectura}mono valor={`${datos.prefijoMovil ?? ''} ${datos.movil ?? ''}`.trim()}>
                        <Space.Compact style={{ width: '100%' }}>
                          <Input disabled={soloLectura} style={{ width: 72 }} placeholder="+34"
                            value={datos.prefijoMovil ?? ''} onChange={(e) => setCampo('prefijoMovil', e.target.value)} />
                          <Input disabled={soloLectura} value={datos.movil ?? ''} onChange={(e) => setCampo('movil', e.target.value)} />
                        </Space.Compact>
                      </CampoOLectura>
                    }
                    existente={vistaExistente('movil', existente?.telefonoMovil)}
                    onCopiar={soloLectura ? undefined : () => copiar('movil', `${datos.prefijoMovil ?? ''}${datos.movil ?? ''}`)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                </FctTarjeta>

                <FctTarjeta titulo="Residencia">
                  <FilaComparada label="Provincia"
                    provisional={<CampoOLectura lectura={soloLectura} valor={etiqueta(opciones.provincias, datos.idProvincia)}><Select disabled={soloLectura} style={{ width: '100%' }} allowClear showSearch
                      optionFilterProp="label" options={opciones.provincias}
                      value={datos.idProvincia ?? undefined} onChange={(v) => setCampo('idProvincia', v ?? null)} /></CampoOLectura>}
                    existente={vistaExistente('provincia', existente?.provincia)}
                    onCopiar={soloLectura ? undefined : () => copiar('provincia', etiqueta(opciones.provincias, datos.idProvincia))}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Código postal"
                    provisional={<CampoOLectura lectura={soloLectura}mono valor={datos.codigoPostal}><Input disabled={soloLectura} value={datos.codigoPostal ?? ''} onChange={(e) => setCampo('codigoPostal', e.target.value)} /></CampoOLectura>}
                    existente={vistaExistente('cp', existente?.codigoPostal)}
                    onCopiar={soloLectura ? undefined : () => copiar('cp', datos.codigoPostal)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                </FctTarjeta>

                <FctTarjeta titulo="Inversor">
                  <FilaComparada label="Inversor"
                    provisional={
                      <CampoOLectura lectura={soloLectura} valor={siNo(datos.inversor)}>
                        <Radio.Group disabled={soloLectura} value={datos.inversor}
                          onChange={(e) => setCampo('inversor', e.target.value)}>
                          <Radio value={true}>Sí</Radio>
                          <Radio value={false}>No</Radio>
                        </Radio.Group>
                      </CampoOLectura>
                    }
                    existente={vistaExistente('inversor', existente ? (existente.tieneFichaCompra ? 'Sí (ficha vigente)' : 'No') : null)}
                    onCopiar={soloLectura ? undefined : () => copiar('inversor', datos.inversor ? 'Sí' : 'No')}
                    copiarDeshabilitado={copiaDeshabilitada} />
                </FctTarjeta>

                <FctTarjeta titulo="Ficha de compra">
                  <FilaComparada label="Tramos facturación" obligatorio={datos.inversor}
                    provisional={<CampoOLectura lectura={soloLectura} valor={tramosProvisional}><Select disabled={soloLectura} mode="multiple" style={{ width: '100%' }}
                      options={opciones.tramos} value={datos.tramosFacturacion}
                      onChange={(v) => setCampo('tramosFacturacion', v)} /></CampoOLectura>}
                    existente={vistaExistente('tramos', tramosExistente)}
                    onCopiar={soloLectura ? undefined : () => copiar('tramos', tramosProvisional)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Provincias interés" obligatorio={datos.inversor}
                    provisional={<CampoOLectura lectura={soloLectura} valor={provinciasProvisionalInteres}><Select disabled={soloLectura} mode="multiple" style={{ width: '100%' }} showSearch
                      optionFilterProp="label" options={opciones.provincias} value={datos.provinciasInteres}
                      onChange={(v) => setCampo('provinciasInteres', v)} /></CampoOLectura>}
                    existente={vistaExistente('provinciasInteres', provinciasExistente)}
                    onCopiar={soloLectura ? undefined : () => copiar('provinciasInteres', provinciasProvisionalInteres)}
                    copiarDeshabilitado={copiaDeshabilitada} />
                  <FilaComparada label="Plazo de compra" obligatorio={datos.inversor}
                    provisional={
                      <Space>
                        <CampoOLectura lectura={soloLectura} valor={etiqueta(opciones.plazos, datos.idPlazoCompra)}>
                          <Select disabled={soloLectura} style={{ width: 220 }} allowClear options={opciones.plazos}
                            value={datos.idPlazoCompra ?? undefined} onChange={(v) => setCampo('idPlazoCompra', v ?? null)} />
                        </CampoOLectura>
                        <Text type="secondary">
                          {fechaLimite ? <>Fecha límite: <span className="fct-mono">{fmtFecha(fechaLimite.toDate())}</span></> : 'Sin fecha límite'}
                        </Text>
                      </Space>
                    }
                    existente={vistaExistente('plazo', existente?.plazoCompra)}
                    onCopiar={soloLectura ? undefined : () => copiar('plazo', etiqueta(opciones.plazos, datos.idPlazoCompra))}
                    copiarDeshabilitado={copiaDeshabilitada} />
                </FctTarjeta>

                <FctTarjeta titulo="Consentimientos">
                  <FilaComparada label="Boletín"
                    provisional={
                      <CampoOLectura lectura={soloLectura} valor={siNo(datos.boletin)}>
                        <Radio.Group disabled={soloLectura} value={datos.boletin}
                          onChange={(e) => setCampo('boletin', e.target.value)}>
                          <Radio value={true}>Sí</Radio>
                          <Radio value={false}>No</Radio>
                        </Radio.Group>
                      </CampoOLectura>
                    }
                    existente={vistaExistente('boletin', null)}
                    onCopiar={soloLectura ? undefined : () => copiar('boletin', datos.boletin ? 'Sí' : 'No')}
                    copiarDeshabilitado={copiaDeshabilitada} />
                </FctTarjeta>

                {caso.origen !== OrigenCaso.Web && (
                  <FctTarjeta titulo="Procedencia">
                    <FilaComparada label="Notas de procedencia"
                      provisional={<CampoOLectura lectura={soloLectura} valor={datos.notasProcedencia}><Input.TextArea disabled={soloLectura} rows={2}
                        value={datos.notasProcedencia ?? ''} onChange={(e) => setCampo('notasProcedencia', e.target.value)} /></CampoOLectura>}
                      existente={<Text type="secondary">—</Text>} />
                  </FctTarjeta>
                )}
              </div>
            </FctTarjeta>
          </Col>

          <Col xs={24} lg={8}>
            {/* Acciones: sin numeración (ajuste 10), siempre habilitadas (ajuste 12).
                En fila horizontal de igual ancho; el texto salta de línea si hace falta
                (aprovecha el ancho ganado al no haber banda de menú a la izquierda). */}
            {!soloLectura && (
              <FctTarjeta style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  {/* Gate D22 (web + email no verificado): Validar y Actualizar deshabilitados; solo
                      Descartar. El motivo lo explica el banner de arriba (no es un botón gris mudo). */}
                  <Button style={estiloBotonAccion} type="primary" loading={accionEnCurso === 'validar'} disabled={gateWebBloqueado || ocupado} onClick={onValidar}>
                    Validar cliente provisional
                  </Button>
                  <Button style={estiloBotonAccion} loading={accionEnCurso === 'actualizar'} disabled={gateWebBloqueado || ocupado} onClick={onActualizar}>
                    Actualizar existente y eliminar provisional
                  </Button>
                  <Button style={estiloBotonAccion} danger loading={accionEnCurso === 'descartar'} disabled={ocupado} onClick={onDescartar}>
                    Descartar alta
                  </Button>
                </div>
              </FctTarjeta>
            )}

            {/* Estado y notas de gestión con histórico (RF-02; altura amplia, ajuste 9) */}
            <FctTarjeta titulo="Estado y notas de gestión">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <EstadoCasoTag estado={caso.estado} />
                <div style={{ minHeight: 220 }}>
                  <List
                    size="small"
                    dataSource={caso.notas}
                    locale={{ emptyText: 'Sin notas' }}
                    renderItem={(n) => (
                      <List.Item>
                        {/* Meta (autor · fecha) arriba y el texto de la nota debajo (como el mockup). */}
                        <div style={{ width: '100%' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {n.autor} · <span className="fct-mono">{fmtFechaHora(n.fecha)}</span>
                          </Text>
                          <div>{n.texto}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                </div>
                {!soloLectura && (
                  <>
                    {/* Añadir nota en línea (input + botón), como el mockup. */}
                    <Space.Compact style={{ width: '100%' }}>
                      <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        placeholder="Añadir nota de gestión…"
                        value={notaNueva}
                        onChange={(e) => setNotaNueva(e.target.value)}
                      />
                      <Button onClick={onAnadirNota} disabled={!notaNueva.trim() || ocupado} loading={accionEnCurso === 'nota'}>
                        Añadir
                      </Button>
                    </Space.Compact>
                    {caso.estado === EstadoCaso.Pendiente ? (
                      <Button block icon={<PauseCircleOutlined />} onClick={() => onEspera(true)} loading={accionEnCurso === 'espera'} disabled={ocupado}>
                        Dejar en espera
                      </Button>
                    ) : (
                      <Button block onClick={() => onEspera(false)} loading={accionEnCurso === 'espera'} disabled={ocupado}>
                        Cambiar a pendiente
                      </Button>
                    )}
                  </>
                )}
              </Space>
            </FctTarjeta>
          </Col>
        </Row>

        <BusquedaCoincidentesModal
          open={busquedaAbierta}
          emailInicial={datos.email}
          movilInicial={datos.movil}
          nombreInicial={`${datos.nombre ?? ''} ${datos.apellido1 ?? ''}`.trim() || null}
          idClienteExcluir={caso.idCliente ?? undefined}
          onSeleccionar={seleccionarCandidato}
          onCerrar={() => setBusquedaAbierta(false)}
        />
      </Space>
    </GcPagina>
  );
};

export default AltasCasoPage;
